/**
 * request.js  —  标准 Vue 版本
 * 用 axios 替换原来的 uni.request / uni.uploadFile
 */
import axios from 'axios'
import { baseUrl } from './config'
import { Toast } from 'vant'
import CryptoJS from 'crypto-js'

const _baseUrl =
  process.env.NODE_ENV === 'development' ? baseUrl : window.location.origin

// ── 工具函数 ──────────────────────────────────────────────────────────────────
const random32 = () => {
  const chars = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678'
  let result = ''
  for (let i = 0; i < 32; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

const cf_aes_key = 'huashankeji'
const encrypt = () => {
  const _timestamp = Math.floor(Date.now() / 1000)
  const _nonce = random32()
  const _sign = CryptoJS.MD5(
    `timestamp=${_timestamp}&nonce=${_nonce}${cf_aes_key}`
  ).toString()
  return { _timestamp, _nonce, _sign }
}

const decrypt = (word) => {
  return typeof word === 'string' ? JSON.parse(word) : word
}

const cleanUserInfo = () => {
  localStorage.setItem('userInfo', '')
  localStorage.setItem('token', '')
  window.location.hash = '#/pages/login/login'
}

const localizedMessage = (message, code) => {
  const fallback =
    typeof message === 'string'
      ? message.trim()
      : message && typeof message.message === 'string'
        ? message.message.trim()
        : message && typeof message.msg === 'string'
          ? message.msg.trim()
          : ''
  const i18n = window.uni && window.uni.$i18n
  if (fallback) {
    if (i18n && typeof i18n.te === 'function' && typeof i18n.t === 'function' && i18n.te(fallback)) {
      return i18n.t(fallback)
    }
    // The API message has priority. Do not replace a useful business message
    // with a generic text merely because the response code is 224.
    return fallback
  }
  const codeKey = { 401: 'API_LOGIN_REQUIRED', 224: 'API_INVALID_REQUEST', 404: 'API_NOT_FOUND', 412: 'API_REQUEST_FAILED', 500: 'API_SERVER_ERROR' }[Number(code)]
  return codeKey && i18n && typeof i18n.te === 'function' && i18n.te(codeKey)
    ? i18n.t(codeKey)
    : 'Request failed'
}

const businessError = payload => {
  const message = localizedMessage(payload && payload.msg, payload && payload.code)
  const error = new Error(message)
  if (payload && typeof payload === 'object') Object.assign(error, payload)
  error.name = 'BusinessError'
  error.message = message
  error.msg = message
  error.__toastShown = true
  return error
}

// ── axios 实例 ────────────────────────────────────────────────────────────────
const http = axios.create({
  baseURL: _baseUrl,
  timeout: 20000
})

// ── request ───────────────────────────────────────────────────────────────────
const request = {}

request.globalRequest = (url, method = 'GET', data = {}, power, isProxy) => {
  const token = localStorage.getItem('token') || ''
  const currentLang = localStorage.getItem('lang') || 'cn'

  const headers = {
    lang: currentLang
  }
  if (power === 2) {
    headers['Authorization'] = token
  }

  const cryptoData =
    method === 'POST' ? Object.assign(encrypt(), data) : data

  const config = {
    url,
    method,
    headers,
    ...(method === 'GET'
      ? { params: cryptoData }
      : { data: cryptoData })
  }

  return http(config)
    .then(res => {
      const status = res.status
      if (status === 401) {
        cleanUserInfo()
        return
      }
      const _data = decrypt(res.data)
      if (_data.code === 200) {
        return _data
      } else if (_data.code === -1) {
        cleanUserInfo()
        throw _data
      } else {
        // Preserve business errors (for example the VIP0 withdrawal
        // restriction) instead of presenting a generic authentication error.
        const error = businessError(_data)
        Toast.fail(error.msg)
        return Promise.reject(error)
      }
    })
    .catch(err => {
      if (err && err.response && err.response.status === 401) {
        cleanUserInfo()
      } else if (err && err.code !== undefined) {
        // 业务错误已在 then 中处理，不再重复
        return Promise.reject(err)
      }
      return Promise.reject(err)
    })
}

request.uploadImg = (url, files) => {
  const token = localStorage.getItem('token') || ''
  const lang  = localStorage.getItem('lang')  || 'cn'

  const formData = new FormData()
  if (files) {
    formData.append('img', files.file || files)
  }
  const extra = encrypt()
  Object.entries(extra).forEach(([k, v]) => formData.append(k, v))

  Toast.loading({ message: '上传中…', forbidClick: true })

  return http
    .post(url, formData, {
      headers: { lang, Authorization: token, 'Content-Type': 'multipart/form-data' }
    })
    .then(res => {
      Toast.clear()
      const _data = decrypt(res.data)
      if (_data.code === 200) return _data
      _data.msg = localizedMessage(_data.msg, _data.code)
      Toast.fail(_data.msg)
      return Promise.reject(_data.msg)
    })
    .catch(err => {
      Toast.fail(err.message || '上传错误')
      return Promise.reject(err)
    })
}

export default request
